import {
  Col,
  Row,
  Space,
  Table,
  Card,
  Button,
  message,
  Spin,
  Input,
  Select,
  Tag,
  Typography,
  Radio,
} from "antd";
import {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useDeferredValue,
} from "react";
import {
  ReloadOutlined,
  InboxOutlined,
  EnvironmentOutlined,
  FilterOutlined,
  FileSearchOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import {
  GET_GEO_ALL,
  HEADERS_EXPORT_BEN,
  POST_MAIN_ENFORCEMENT,
} from "../../API/apiUrls";
import axios from "axios";
import MotionHoc from "../../../utils/MotionHoc";
import TokenCheck from "../../../hook/TokenCheck";
import {
  ASSET_TYPE_META,
  CONTRACT_SOURCE,
  getAssetTypeMeta,
  parseIdCardArea,
} from "./constant/ConstantCustom";

const { Option } = Select;
const { Text } = Typography;

/* ------------------------------------------------------------------ */
/* สถานะการสืบ — single source of truth                                 */
/* ใช้ร่วมกัน: dropdown / filter / tag กลุ่ม / tag รายแปลง                */
/* ------------------------------------------------------------------ */
const STATUS_META = {
  1: { label: "รอลงพื้นที่", color: "orange", icon: "📤" },
  2: { label: "ดำเนินการสืบ", color: "blue", icon: "🕒" },
  // 3: { label: "สืบสำเร็จ", color: "green", icon: "✅" },
};
const STATUS_KEYS = Object.keys(STATUS_META).map(Number); // [1,2,3]

const statusOptions = STATUS_KEYS.map((value) => ({
  value,
  ...STATUS_META[value],
}));

const StatusTag = ({ status }) => {
  const m = STATUS_META[status];
  if (!m) return <Tag>ไม่ระบุ</Tag>;
  return <Tag color={m.color}>{m.label}</Tag>;
};

const norm = (v) =>
  String(v ?? "")
    .trim()
    .toLowerCase();

const fullName = (r) =>
  `${r.SNAM || ""}${r.NAME1 || ""} ${r.NAME2 || ""}`.trim();

// ลำดับจำเลยจาก judgeDef (null = ไม่อยู่ในรายชื่อจำเลย)
const defendantLabel = (n) => (n ? `จำเลยที่ ${n}` : "บุคคลอื่นในสัญญา");

// รายชื่อจำเลยของสัญญา ถ้า API ไม่ส่ง judgeDef มา ใช้ลูกหนี้หลักเป็นจำเลยที่ 1
const defendantsOf = (r) =>
  Array.isArray(r.judgeDef) && r.judgeDef.length > 0
    ? r.judgeDef
    : [
        {
          defendantNumber: 1,
          customerId: r.customerId,
          CUSCOD: r.CUSCOD,
          SNAM: r.SNAM,
          NAME1: r.NAME1,
          NAME2: r.NAME2,
        },
      ];

const ZONE_GRADE_COLOR = { 1: "green", 2: "blue", 3: "orange", 4: "red" };

// sendStatus 3 / active 0 = ถอนเคสกลับ -> ไม่นำมาเลือกลงพื้นที่
const isWithdrawn = (r) => r.sendStatus === 3 || r.active === 0;

// flagOnsite ของทรัพย์ -> สถานะการสืบของหน้านี้ (0 = รอลงพื้นที่, 1 = ดำเนินการสืบ)
const onsiteToInvestigation = (flagOnsite) => (flagOnsite ? 2 : 1);

const ASSET_TYPE_KEYS = Object.keys(ASSET_TYPE_META).map(Number); // [1..5]

const AREA_SOURCE_META = {
  asset: { label: "ทรัพย์", color: "green" },
  idCard: { label: "บัตร ปชช.", color: "gold" },
};

// ตัด "จ." / "จังหวัด" ออกก่อนเทียบชื่อจังหวัด
const normProvName = (v) =>
  String(v ?? "")
    .replace(/^(จังหวัด|จ\.)\s*/, "")
    .trim();

// รายละเอียดทรัพย์ตามประเภท (ที่ดิน / รถ / อายัดเงิน)
const assetDetailText = (r) => {
  const meta = getAssetTypeMeta(r.typeAsset);
  if (meta.isLand) {
    const t = r.typeLand === 1 ? "โฉนด" : r.typeLand === 2 ? "นส.3" : "";
    return [t, r.numberLand].filter(Boolean).join(" ") || "-";
  }
  if (meta.isVehicle)
    return (
      [r.vhLicensePlate, r.vhType, r.vhColor].filter(Boolean).join(" · ") ||
      r.vhChassisNo ||
      "-"
    );
  return r.incomeSource || "-";
};

const Main = () => {
  /* ----------------------------- States ---------------------------- */
  const [assignments, setAssignments] = useState([]); // raw จาก POST_MAIN_ENFORCEMENT
  const [loading, setLoading] = useState(false);
  const [loadingGeo, setLoadingGeo] = useState(false);
  const [searchInputTable, setSearchInputTable] = useState("");
  const [processStatus, setProcessStatus] = useState(1); // null = ทั้งหมด
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [contractSource, setContractSource] = useState("all"); // key ของ CONTRACT_SOURCE
  const [assetTypeFilter, setAssetTypeFilter] = useState(null); // null = ทั้งหมด

  const roleId = parseInt(localStorage.getItem("ROLE_ID"));
  const userId = parseInt(localStorage.getItem("USER_ID"));
  const [signOut] = TokenCheck();

  // geo raw — map จังหวัด -> โซน (groupArea/groupDesc) + แปลงรหัส อำเภอ/ตำบล เป็นชื่อ
  const [geo, setGeo] = useState({
    province: [],
    district: [],
    subDistrict: [],
  });

  // geo selections (เก็บเป็น "code" ระดับนั้น ๆ)
  const [selZone, setSelZone] = useState(null); // groupArea (number)
  const [selProvince, setSelProvince] = useState(null); // provCode (string)
  const [selDistrict, setSelDistrict] = useState(null); // distCode (string)
  const [selSubDistrict, setSelSubDistrict] = useState(null); // subDistCode (string)

  /* --------------------------- Load GEO ---------------------------- */
  useEffect(() => {
    let isMounted = true;
    const loadGeoData = async () => {
      setLoadingGeo(true);
      try {
        const res = await axios.get(GET_GEO_ALL, {
          headers: HEADERS_EXPORT_BEN,
        });
        const d = res.data || {};
        if (isMounted) {
          setGeo({
            province: d.province || [],
            district: d.district || [],
            subDistrict: d.subDistrict || [],
          });
        }
      } catch (error) {
        if (error.name !== "CanceledError" && isMounted) {
          message.error("ไม่สามารถโหลดข้อมูลพื้นที่ได้");
          console.error("❌ Error loading geo data:", error);
        }
      } finally {
        if (isMounted) setLoadingGeo(false);
      }
    };
    loadGeoData();
    return () => {
      isMounted = false;
    };
  }, []);

  /* ------------- geo lookup (Map) — สร้างครั้งเดียวต่อการโหลด ------- */
  const geoIndex = useMemo(() => {
    const prov = new Map();
    const provByName = new Map();
    geo.province.forEach((x) => {
      prov.set(String(x.provCode), x);
      provByName.set(normProvName(x.provDesc), x.provCode);
    });
    const dist = new Map();
    geo.district.forEach((x) => dist.set(`${x.provCode}|${x.distCode}`, x));
    const subDist = new Map();
    geo.subDistrict.forEach((x) =>
      subDist.set(`${x.provCode}|${x.distCode}|${x.subDistCode}`, x),
    );
    return { prov, provByName, dist, subDist };
  }, [geo]);

  /* ----------------------- helper: prov -> zone -------------------- */
  const provToZone = useCallback(
    (provCode) => geoIndex.prov.get(String(provCode))?.groupArea ?? null,
    [geoIndex],
  );

  const provToZoneDesc = useCallback(
    (provCode) => geoIndex.prov.get(String(provCode))?.groupDesc ?? null,
    [geoIndex],
  );

  const getProvinceDesc = useCallback(
    (provCode) =>
      geoIndex.prov.get(String(provCode))?.provDesc ||
      (provCode ? String(provCode) : "-"),
    [geoIndex],
  );

  const getDistrictDesc = useCallback(
    (distCode, provCode) =>
      geoIndex.dist.get(`${provCode}|${distCode}`)?.distDesc ||
      (distCode ? String(distCode) : "-"),
    [geoIndex],
  );

  const getSubDistrictDesc = useCallback(
    (subDistCode, distCode, provCode) =>
      geoIndex.subDist.get(`${provCode}|${distCode}|${subDistCode}`)
        ?.subDistDesc || (subDistCode ? String(subDistCode) : "-"),
    [geoIndex],
  );

  // ชื่อจังหวัด (เช่น vhRegProvince) -> provCode
  const provCodeByName = useCallback(
    (name) => {
      const n = normProvName(name);
      return n ? (geoIndex.provByName.get(n) ?? null) : null;
    },
    [geoIndex],
  );

  // รหัสอำเภอ 2 หลักจากบัตร -> distCode ของ geo (รองรับทั้งแบบ "12" และ "4112")
  const resolveDistCode = useCallback(
    (provCode, dd) => {
      for (const code of [dd, `${provCode}${dd}`, String(Number(dd))]) {
        if (geoIndex.dist.has(`${provCode}|${code}`)) return code;
      }
      return dd;
    },
    [geoIndex],
  );

  // พื้นที่ตามบัตรประชาชนของสัญญา
  const idCardArea = useCallback(
    (cuscod) => {
      const a = parseIdCardArea(cuscod);
      if (!a) return { province: null, district: null };
      return {
        province: a.province,
        district: resolveDistCode(a.province, a.district),
      };
    },
    [resolveDistCode],
  );

  // ── Query ─────────────────────────────────────────────
  const queryData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.post(
        POST_MAIN_ENFORCEMENT,
        { userId: userId === 4 ? 1 : userId },
        { headers: HEADERS_EXPORT_BEN },
      );
      setAssignments(
        Array.isArray(res.data?.data)
          ? res.data.data.filter((item) => item.receiveDate)
          : [],
      );
    } catch (err) {
      message.error(
        err?.response?.status === 404 ? "ไม่พบข้อมูล" : "เกิดข้อผิดพลาด",
      );
      if (err?.response?.status === 401) {
        signOut();
      }
      setAssignments([]);
    } finally {
      setLoading(false);
    }
  }, [signOut, userId]);

  useEffect(() => {
    queryData();
  }, []);

  const activeAssignments = useMemo(
    () => assignments.filter((r) => !isWithdrawn(r)),
    [assignments],
  );

  // ข้อมูลลูกค้า/สัญญาที่ใช้ร่วมกันทุกแถว — ชื่อ/เลขบัตรของเจ้าของ หาจาก judgeDef
  const contractFields = (r, customerId) => {
    const d = defendantsOf(r).find((x) => x.customerId === customerId);
    return {
      idAssignment: r.idAssignment,
      idJudge: r.idJudge,
      contno: r.newContno,
      redCaseNumber: r.redCaseNumber,
      stageText: r.stageText,
      receiveByName: r.receiveByName,
      customerId,
      CUSCOD: d?.CUSCOD || "",
      SNAM: d?.SNAM || "",
      NAME1: d ? d.NAME1 : `รหัสลูกค้า ${customerId}`,
      NAME2: d?.NAME2 || "",
      defendantNumber: d?.defendantNumber ?? null,
    };
  };

  const withAreaDesc = (row) => ({
    ...row,
    provDesc: getProvinceDesc(row.province),
    distDesc: getDistrictDesc(row.district, row.province),
    subDistDesc: getSubDistrictDesc(
      row.subdistrict,
      row.district,
      row.province,
    ),
  });

  // แตก assignments -> ทรัพย์รายรายการ (1 record = 1 asset) เฉพาะเคสที่ยังไม่ถอน
  // พื้นที่: รถในสัญญาใช้ตามบัตรประชาชน / ที่ดินใช้ที่ตั้งทรัพย์ / รถนอกสัญญาใช้จังหวัดทะเบียน
  //         หาไม่ได้ใช้ตามบัตรประชาชน
  const assetRows = useMemo(
    () =>
      activeAssignments.flatMap((r) =>
        (r.assets || []).map((a) => {
          let area = {
            province: a.province,
            district: a.district,
            subdistrict: a.subdistrict,
          };
          const owner = contractFields(r, a.customerId);
          let areaSource = "asset";
          if (a.typeAsset === 4) {
            area = { ...idCardArea(owner.CUSCOD), subdistrict: null };
            areaSource = "idCard";
          } else if (area.province == null) {
            const vhProv = getAssetTypeMeta(a.typeAsset).isVehicle
              ? provCodeByName(a.vhRegProvince)
              : null;
            if (vhProv != null) {
              area = { province: vhProv, district: null, subdistrict: null };
            } else {
              area = { ...idCardArea(owner.CUSCOD), subdistrict: null };
              areaSource = "idCard";
            }
          }
          return withAreaDesc({
            ...owner,
            rowId: `a-${a.idAsset}`,
            idAsset: a.idAsset,
            assetKey: a.assetKey,
            loanId: a.loanId ?? r.loanId,
            investigation: onsiteToInvestigation(a.flagOnsite),
            typeAsset: a.typeAsset,
            numberLand: a.numberLand,
            typeLand: a.typeLand,
            vhLicensePlate: a.vhLicensePlate,
            vhType: a.vhType,
            vhColor: a.vhColor,
            vhChassisNo: a.vhChassisNo,
            incomeSource: a.incomeSource,
            zoneGrade: a.zoneGrade,
            areaSource,
            ...area,
          });
        }),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      activeAssignments,
      provCodeByName,
      idCardArea,
      getProvinceDesc,
      getDistrictDesc,
      getSubDistrictDesc,
    ],
  );

  // 1 สัญญา × 1 จำเลย = 1 แถว พื้นที่ตามเลขบัตรประชาชน (หลัก 2-3 จังหวัด / 4-5 อำเภอ)
  const idCardRows = useMemo(
    () =>
      activeAssignments.flatMap((r) =>
        defendantsOf(r).map((d) => {
          const ownAssets = (r.assets || []).filter(
            (a) => a.customerId === d.customerId,
          );
          return withAreaDesc({
            ...contractFields(r, d.customerId),
            rowId: `c-${r.idAssignment}-${d.customerId}`,
            loanId: r.loanId,
            // มีทรัพย์ของจำเลยคนนี้ที่ลงพื้นที่แล้วอย่างน้อย 1 รายการ = ดำเนินการสืบ
            investigation: onsiteToInvestigation(
              ownAssets.some((a) => a.flagOnsite),
            ),
            typeAsset: null,
            areaSource: "idCard",
            ...idCardArea(d.CUSCOD),
            subdistrict: null,
          });
        }),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeAssignments, idCardArea, getProvinceDesc, getDistrictDesc],
  );

  // ทรัพย์แยกตาม สัญญา|เจ้าของ — ใช้แสดงรายการย่อยของโหมดบัตร
  const assetsByOwner = useMemo(() => {
    const map = new Map();
    assetRows.forEach((a) => {
      const key = `${a.idAssignment}|${a.customerId}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(a);
    });
    return map;
  }, [assetRows]);

  const isIdCardMode = contractSource === "idCard";

  /* ------------------------------------------------------------------ */
  /* rows ที่ผ่าน สถานะ + ค้นหา (ยังไม่กรองแหล่ง/ประเภท/พื้นที่)         */
  /* ใช้นับจำนวนบนปุ่มแหล่งสัญญา และ option ประเภททรัพย์                 */
  /* ------------------------------------------------------------------ */
  // กรองตามค่าค้นหาแบบ deferred ไม่ให้ช่องค้นหาหน่วงตอนข้อมูลเยอะ
  const deferredSearch = useDeferredValue(searchInputTable);
  const matchesStatusSearch = useCallback(
    (item) => {
      if (processStatus != null && item.investigation !== processStatus)
        return false;
      const q = norm(deferredSearch);
      if (q) {
        const hay = [
          item.contno,
          item.CUSCOD,
          item.redCaseNumber,
          fullName(item),
        ]
          .map(norm)
          .some((v) => v.includes(q));
        if (!hay) return false;
      }
      return true;
    },
    [processStatus, deferredSearch],
  );

  const statusAssetRows = useMemo(
    () => assetRows.filter(matchesStatusSearch),
    [assetRows, matchesStatusSearch],
  );
  const statusIdCardRows = useMemo(
    () => idCardRows.filter(matchesStatusSearch),
    [idCardRows, matchesStatusSearch],
  );

  const sourceCounts = useMemo(() => {
    const counts = { idCard: statusIdCardRows.length };
    ["all", "inContract", "found"].forEach((k) => {
      counts[k] = statusAssetRows.filter((r) =>
        CONTRACT_SOURCE[k].typeAssets.includes(r.typeAsset),
      ).length;
    });
    return counts;
  }, [statusAssetRows, statusIdCardRows]);

  // option ประเภททรัพย์ เฉพาะที่อยู่ในแหล่งที่เลือก พร้อมจำนวน
  const assetTypeOptions = useMemo(() => {
    if (isIdCardMode) return [];
    const allowed = CONTRACT_SOURCE[contractSource].typeAssets;
    return ASSET_TYPE_KEYS.filter((k) => allowed.includes(k)).map((k) => ({
      value: k,
      label: ASSET_TYPE_META[k].label,
      count: statusAssetRows.filter((r) => r.typeAsset === k).length,
    }));
  }, [isIdCardMode, contractSource, statusAssetRows]);

  /* ------------------------------------------------------------------ */
  /* pool สำหรับสร้าง option พื้นที่ = data ที่ผ่าน แหล่ง + ประเภท + สถานะ + ค้นหา */
  /* (ยังไม่กรองพื้นที่ เพื่อไม่ให้ dropdown ที่กำลังเลือกอยู่ว่างเปล่า)    */
  /* ------------------------------------------------------------------ */
  const areaSourceRows = useMemo(() => {
    if (isIdCardMode) return statusIdCardRows;
    const allowed = CONTRACT_SOURCE[contractSource].typeAssets;
    return statusAssetRows.filter(
      (r) =>
        allowed.includes(r.typeAsset) &&
        (assetTypeFilter == null || r.typeAsset === assetTypeFilter),
    );
  }, [
    isIdCardMode,
    contractSource,
    assetTypeFilter,
    statusAssetRows,
    statusIdCardRows,
  ]);

  /* ---------------- Cascading options (derive จาก data) ------------ */
  // โซน: เฉพาะโซนที่มีทรัพย์จริง
  const zoneOptions = useMemo(() => {
    const map = new Map();
    areaSourceRows.forEach((r) => {
      const ga = provToZone(r.province);
      if (ga != null && !map.has(ga))
        map.set(ga, { value: ga, label: provToZoneDesc(r.province) });
    });
    return [...map.values()];
  }, [areaSourceRows, provToZone, provToZoneDesc]);

  // จังหวัด: เฉพาะที่มีทรัพย์ -> scope ด้วยโซน
  const provinceOptions = useMemo(() => {
    const map = new Map();
    areaSourceRows.forEach((r) => {
      if (selZone != null && provToZone(r.province) !== selZone) return;
      if (!map.has(r.province))
        map.set(r.province, { value: String(r.province), label: r.provDesc });
    });
    return [...map.values()];
  }, [areaSourceRows, selZone, provToZone]);

  // อำเภอ: เฉพาะที่มีทรัพย์ -> scope ด้วยจังหวัด, value composite กันชนข้ามจังหวัด
  const districtOptions = useMemo(() => {
    const map = new Map();
    areaSourceRows.forEach((r) => {
      if (selProvince != null && String(r.province) !== String(selProvince))
        return;
      const key = `${r.province}|${r.district}`;
      if (!map.has(key)) map.set(key, { value: key, label: r.distDesc });
    });
    return [...map.values()];
  }, [areaSourceRows, selProvince]);

  // ตำบล: ต้องเลือกจังหวัดก่อน -> scope ด้วยจังหวัด + อำเภอ
  const subDistrictOptions = useMemo(() => {
    if (selProvince == null) return [];
    const map = new Map();
    areaSourceRows.forEach((r) => {
      if (String(r.province) !== String(selProvince)) return;
      if (selDistrict != null && String(r.district) !== String(selDistrict))
        return;
      const key = `${r.province}|${r.district}|${r.subdistrict}`;
      if (!map.has(key)) map.set(key, { value: key, label: r.subDistDesc });
    });
    return [...map.values()];
  }, [areaSourceRows, selProvince, selDistrict]);

  /* --------------------------- Lock flags -------------------------- */
  const lockZone =
    selProvince != null || selDistrict != null || selSubDistrict != null;
  const lockProvince = selDistrict != null || selSubDistrict != null;
  const lockDistrict = selSubDistrict != null;

  const hasAreaFilter =
    selZone != null ||
    selProvince != null ||
    selDistrict != null ||
    selSubDistrict != null;

  /* ---------------------- Change handlers (auto-fill) -------------- */
  const handleZoneChange = useCallback((z) => {
    setSelZone(z ?? null);
    setSelProvince(null);
    setSelDistrict(null);
    setSelSubDistrict(null);
  }, []);

  const handleProvinceChange = useCallback(
    (provCode) => {
      if (provCode == null) {
        setSelProvince(null);
        setSelDistrict(null);
        setSelSubDistrict(null);
        return;
      }
      setSelProvince(provCode);
      setSelZone(provToZone(provCode));
      setSelDistrict(null);
      setSelSubDistrict(null);
    },
    [provToZone],
  );

  const handleDistrictChange = useCallback(
    (composite) => {
      if (!composite) {
        setSelDistrict(null);
        setSelSubDistrict(null);
        return;
      }
      const [provCode, distCode] = composite.split("|");
      setSelProvince(provCode);
      setSelZone(provToZone(provCode));
      setSelDistrict(distCode);
      setSelSubDistrict(null);
    },
    [provToZone],
  );

  const handleSubDistrictChange = useCallback(
    (composite) => {
      if (!composite) {
        setSelSubDistrict(null);
        return;
      }
      const [provCode, distCode, subCode] = composite.split("|");
      setSelProvince(provCode);
      setSelZone(provToZone(provCode));
      setSelDistrict(distCode);
      setSelSubDistrict(subCode);
    },
    [provToZone],
  );

  const handleResetArea = useCallback(() => {
    setSelZone(null);
    setSelProvince(null);
    setSelDistrict(null);
    setSelSubDistrict(null);
  }, []);

  const handleResetFilter = useCallback(() => {
    setSearchInputTable("");
    setProcessStatus(null);
    setContractSource("all");
    setAssetTypeFilter(null);
    setSelectedRowKeys([]);
    handleResetArea();
    message.info("ล้างตัวกรองสำเร็จ");
  }, [handleResetArea]);

  // เปลี่ยนแหล่งสัญญา -> groupKey เปลี่ยน จึงล้างรายการที่เลือก + พื้นที่ + ประเภททรัพย์
  const handleSourceChange = useCallback(
    (e) => {
      setContractSource(e.target.value);
      setAssetTypeFilter(null);
      setSelectedRowKeys([]);
      handleResetArea();
    },
    [handleResetArea],
  );

  /* ----------------- ค่า value ของ select (composite) -------------- */
  const districtValue =
    selProvince != null && selDistrict != null
      ? `${selProvince}|${selDistrict}`
      : undefined;
  const subDistrictValue =
    selProvince != null && selDistrict != null && selSubDistrict != null
      ? `${selProvince}|${selDistrict}|${selSubDistrict}`
      : undefined;

  /* --------------------- กรองพื้นที่ (ต่อยอดจาก areaSourceRows) ----- */
  const matchesLocation = useCallback(
    (rec) => {
      if (!hasAreaFilter) return true;
      if (
        selSubDistrict != null &&
        String(rec.subdistrict) !== String(selSubDistrict)
      )
        return false;
      if (selDistrict != null && String(rec.district) !== String(selDistrict))
        return false;
      if (selProvince != null && String(rec.province) !== String(selProvince))
        return false;
      if (selZone != null && provToZone(rec.province) !== selZone) return false;
      return true;
    },
    [
      hasAreaFilter,
      selZone,
      selProvince,
      selDistrict,
      selSubDistrict,
      provToZone,
    ],
  );

  const filteredLands = useMemo(
    () => areaSourceRows.filter(matchesLocation),
    [areaSourceRows, matchesLocation],
  );

  // group ตาม แหล่ง + เลขสัญญา + customer + จังหวัด
  const groupedTable = useMemo(() => {
    const groups = new Map();
    for (const rec of filteredLands) {
      const key = `${contractSource}|${rec.contno}|${rec.customerId}|${rec.province}`;
      if (!groups.has(key)) {
        groups.set(key, {
          groupKey: key,
          idAssignment: rec.idAssignment,
          contno: rec.contno,
          redCaseNumber: rec.redCaseNumber,
          stageText: rec.stageText,
          receiveByName: rec.receiveByName,
          customerId: rec.customerId,
          CUSCOD: rec.CUSCOD,
          SNAM: rec.SNAM,
          NAME1: rec.NAME1,
          NAME2: rec.NAME2,
          defendantNumber: rec.defendantNumber,
          province: rec.province,
          provDesc: rec.provDesc,
          zoneDesc: provToZoneDesc(rec.province),
          items: [],
        });
      }
      groups.get(key).items.push(rec);
    }

    const arr = [...groups.values()].map((g) => {
      const statusCounts = STATUS_KEYS.reduce((acc, k) => {
        acc[k] = 0;
        return acc;
      }, {});
      g.items.forEach((l) => {
        if (statusCounts[l.investigation] != null)
          statusCounts[l.investigation] += 1;
      });
      // โหมดบัตร: 1 แถว = 1 สัญญา -> รายการย่อยคือทรัพย์ทั้งหมดของสัญญา
      const details = isIdCardMode
        ? assetsByOwner.get(`${g.idAssignment}|${g.customerId}`) || []
        : g.items;
      const areaSources = [...new Set(g.items.map((l) => l.areaSource))];
      return {
        ...g,
        details,
        count: isIdCardMode ? details.length : g.items.length,
        areaSources,
        statusCounts,
      };
    });

    // เรียงตามเลขสัญญาเป็นหลัก -> ลำดับจำเลย (ไม่อยู่ในรายชื่อไว้ท้าย) -> จังหวัด
    arr.sort((a, b) => {
      const c = String(a.contno).localeCompare(String(b.contno), "th");
      if (c !== 0) return c;
      const da = a.defendantNumber ?? Infinity;
      const db = b.defendantNumber ?? Infinity;
      if (da !== db) return da - db;
      return String(a.provDesc || "").localeCompare(
        String(b.provDesc || ""),
        "th",
      );
    });
    return arr;
  }, [
    filteredLands,
    provToZoneDesc,
    contractSource,
    isIdCardMode,
    assetsByOwner,
  ]);

  const totalLands = useMemo(
    () => groupedTable.reduce((s, g) => s + g.count, 0),
    [groupedTable],
  );

  /* --------------------------- Selection --------------------------- */
  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
  };

  /* ------------------ สถานะรวมของกลุ่ม (parent) -------------------- */
  const renderGroupStatus = (g) => {
    const present = STATUS_KEYS.filter((k) => g.statusCounts[k] > 0);
    if (present.length === 0) return <span style={{ color: "#bbb" }}>-</span>;
    return (
      <Space size={4} wrap>
        {present.map((k) => (
          <Tag key={k} color={STATUS_META[k].color}>
            {STATUS_META[k].label} ({g.statusCounts[k]})
          </Tag>
        ))}
      </Space>
    );
  };

  /* --------------- ตารางย่อย: รายละเอียดทรัพย์แต่ละรายการ ---------- */
  const expandedRowRender = (group) => {
    const detailColumns = [
      {
        title: "ลำดับ",
        key: "i",
        align: "center",
        width: 70,
        render: (_, __, i) => i + 1,
      },
      {
        title: "ประเภททรัพย์",
        dataIndex: "typeAsset",
        key: "typeAsset",
        align: "center",
        render: (v) => <Tag color="cyan">{getAssetTypeMeta(v).label}</Tag>,
      },
      {
        title: "รายละเอียดทรัพย์",
        key: "detail",
        align: "left",
        render: (_, r) => assetDetailText(r),
      },
      {
        title: "จังหวัด",
        dataIndex: "provDesc",
        key: "provDesc",
        align: "center",
      },
      {
        title: "อำเภอ",
        dataIndex: "distDesc",
        key: "distDesc",
        align: "center",
      },
      {
        title: "ตำบล",
        dataIndex: "subDistDesc",
        key: "subDistDesc",
        align: "center",
      },
      {
        title: "เกรดโซน",
        dataIndex: "zoneGrade",
        key: "zoneGrade",
        align: "center",
        render: (v) =>
          v != null ? (
            <Tag color={ZONE_GRADE_COLOR[v] || "default"}>{v}</Tag>
          ) : (
            "-"
          ),
      },
      {
        title: "สถานะการสืบ",
        key: "inv",
        align: "center",
        render: (_, r) => <StatusTag status={r.investigation} />,
      },
      {
        title: "จัดการ",
        key: "action",
        align: "center",
        width: 130,
        render: (_, r) => (
          <>
            {processStatus === 3 ? (
              <Button
                size="small"
                icon={<FileSearchOutlined />}
                onClick={() => {
                  // TODO: เปิด modal รายละเอียดทรัพย์จริงตรงนี้
                  console.log("ดูข้อมูลทรัพย์:", r);
                  message.info(`ดูข้อมูลทรัพย์ ${assetDetailText(r)}`);
                }}
              >
                ดูข้อมูล
              </Button>
            ) : processStatus === 2 ? (
              <Button
                size="small"
                icon={<FileSearchOutlined />}
                onClick={() => {
                  // TODO: เปิด modal รายละเอียดทรัพย์จริงตรงนี้
                  console.log("ดูข้อมูลทรัพย์:", r);
                  message.info(`ดูข้อมูลทรัพย์ ${assetDetailText(r)}`);
                }}
              >
                ลงข้อมูลทรัพย์
              </Button>
            ) : null}
          </>
        ),
      },
    ];

    return (
      <Table
        size="small"
        columns={detailColumns}
        dataSource={group.details}
        rowKey={(r) => r.rowId}
        pagination={false}
        style={{ background: "#fafafa", borderRadius: 8 }}
      />
    );
  };

  /* --------------------- คอลัมน์หลัก (parent) --------------------- */
  const columns = [
    {
      title: "ลำดับ",
      key: "index",
      align: "center",
      width: 70,
      render: (_, __, index) => index + 1,
    },
    {
      title: "เลขสัญญา",
      dataIndex: "contno",
      key: "contno",
      align: "center",
      width: 140,
      sorter: (a, b) => String(a.contno).localeCompare(String(b.contno), "th"),
      defaultSortOrder: "ascend",
      render: (v, g) => (
        <div style={{ lineHeight: 1.6 }}>
          <Text strong>{v}</Text>
          {g.redCaseNumber && (
            <div style={{ fontSize: 12, color: "#888" }}>
              คดีแดง {g.redCaseNumber}
            </div>
          )}
          {g.stageText && (
            <div style={{ fontSize: 12, color: "#888" }}>{g.stageText}</div>
          )}
        </div>
      ),
    },
    {
      title: "ชื่อ-นามสกุลเจ้าของทรัพย์",
      key: "fullName",
      align: "left",
      render: (_, g) => fullName(g),
    },
    {
      title: "จำเลย",
      key: "defendantNumber",
      align: "left",
      render: (_, g) => defendantLabel(g.defendantNumber),
    },
    {
      title: "โซน",
      key: "zone",
      align: "center",
      render: (_, g) =>
        g.zoneDesc ? (
          <Tag color="geekblue">{g.zoneDesc}</Tag>
        ) : (
          <span style={{ color: "#bbb" }}>-</span>
        ),
    },
    {
      title: "จังหวัด",
      dataIndex: "provDesc",
      key: "province",
      align: "center",
    },
    {
      title: "ที่มาพื้นที่",
      key: "areaSource",
      align: "center",
      width: 110,
      render: (_, g) => (
        <Space size={4} wrap>
          {g.areaSources.map((k) => (
            <Tag key={k} color={AREA_SOURCE_META[k].color}>
              {AREA_SOURCE_META[k].label}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: "จำนวนทรัพย์",
      key: "count",
      align: "center",
      width: 110,
      render: (_, g) => <Tag color="purple">{g.count} รายการ</Tag>,
    },
    {
      title: "สถานะการสืบ",
      key: "investigation",
      align: "center",
      width: 180,
      render: (_, g) => renderGroupStatus(g),
    },
  ];

  /* ----------------------- Select รวม style ------------------------ */
  const geoSelectProps = {
    size: "large",
    showSearch: true,
    allowClear: true,
    optionFilterProp: "children",
    style: { minWidth: 200 },
  };

  const stepBadge = (n, color) => (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 22,
        height: 22,
        borderRadius: "50%",
        background: color,
        color: "#fff",
        fontSize: 12,
        fontWeight: 700,
      }}
    >
      {n}
    </span>
  );

  /* ----------------------------- Render ---------------------------- */
  return (
    <Spin
      spinning={loading || loadingGeo}
      size="large"
      tip="กำลังโหลดข้อมูล..."
    >
      {/* ========== ขั้นที่ 1 : สถานะ + ค้นหา (อยู่บน) ========== */}
      <Card
        style={{
          marginBottom: 8,
          borderRadius: 12,
          border: "1px solid #ffcb05",
          background: "#fffdf0",
        }}
      >
        <Row align="middle" gutter={[8, 8]}>
          <Col xs={24} md={10}>
            <Space size={8} align="center">
              {stepBadge("1", "#efa420")}
              <FilterOutlined style={{ color: "#efa420" }} />
              <Text strong style={{ color: "#efa420" }}>
                เลือกสถานะงาน
              </Text>
              <Select
                size="large"
                style={{ minWidth: 210 }}
                placeholder="เลือกสถานะ"
                value={processStatus}
                allowClear
                onChange={(v) => setProcessStatus(v ?? null)}
                optionLabelProp="label"
              >
                {statusOptions.map(({ value, icon, label }) => (
                  <Option key={value} value={value} label={`${icon} ${label}`}>
                    <Space>
                      <span>{icon}</span>
                      <span>{label}</span>
                    </Space>
                  </Option>
                ))}
              </Select>
            </Space>
          </Col>

          <Col xs={24} md={14} style={{ textAlign: "end" }}>
            <Space size={10} wrap>
              <Button
                type="primary"
                ghost
                danger
                icon={<InboxOutlined />}
                disabled={processStatus === 1 && selectedRowKeys.length === 0}
              >
                📥 เลือก ({selectedRowKeys.length})
              </Button>

              <Input
                placeholder="ค้นหาเลขสัญญา, ชื่อ, คดีแดง"
                value={searchInputTable}
                onChange={(e) => setSearchInputTable(e.target.value)}
                style={{ width: 240, borderRadius: 8 }}
                size="small"
                allowClear
                suffix={<span>🔍</span>}
              />
              <Button
                icon={<ReloadOutlined />}
                onClick={handleResetFilter}
                size="large"
                style={{
                  borderRadius: 8,
                  backgroundColor: "#fff1f0",
                  borderColor: "#ffa39e",
                  color: "#cf1322",
                }}
              >
                ล้างตัวกรอง
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* ========== ขั้นที่ 2 : เลือกสัญญาจาก + ประเภททรัพย์ ========== */}
      <Card
        style={{
          marginBottom: 8,
          borderRadius: 12,
          border: "1px solid #b7eb8f",
          background: "#f6ffed",
        }}
      >
        <Row align="middle" gutter={[8, 12]}>
          <Col xs={24} xl={14}>
            <Space size={8} align="center" wrap>
              {stepBadge("2", "#389e0d")}
              <FileTextOutlined style={{ color: "#389e0d" }} />
              <Text strong style={{ color: "#389e0d" }}>
                เลือกสัญญาจาก
              </Text>
              <Radio.Group
                value={contractSource}
                onChange={handleSourceChange}
                buttonStyle="solid"
              >
                {Object.entries(CONTRACT_SOURCE).map(([k, m]) => (
                  <Radio.Button key={k} value={k}>
                    {m.icon} {m.label} ({sourceCounts[k] ?? 0})
                  </Radio.Button>
                ))}
              </Radio.Group>
            </Space>
          </Col>
          <Col xs={24} xl={10}>
            <Space size={8} align="center" wrap>
              <Text strong>ประเภททรัพย์</Text>
              <Select
                size="large"
                style={{ minWidth: 260 }}
                value={assetTypeFilter ?? "all"}
                disabled={isIdCardMode}
                onChange={(v) => setAssetTypeFilter(v === "all" ? null : v)}
              >
                <Option value="all">
                  ทั้งหมด ({isIdCardMode ? 0 : sourceCounts[contractSource]})
                </Option>
                {assetTypeOptions.map((o) => (
                  <Option key={o.value} value={o.value}>
                    {o.value}. {o.label} ({o.count})
                  </Option>
                ))}
              </Select>
              {isIdCardMode && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  ใช้พื้นที่ตามบัตรประชาชน (หลัก 2-3 จังหวัด / 4-5 อำเภอ)
                </Text>
              )}
            </Space>
          </Col>
        </Row>
      </Card>

      {/* ========== ขั้นที่ 3 : กรองตามพื้นที่ (อยู่ล่าง) ========== */}
      <Card
        style={{
          marginBottom: 8,
          borderRadius: 12,
          border: "1px solid #91caff",
          background: "#f5faff",
        }}
      >
        <Row align="middle" style={{ marginBottom: 12 }} gutter={8}>
          <Col flex="auto">
            <Space size={8} align="center">
              {stepBadge("3", "#1677ff")}
              <EnvironmentOutlined style={{ color: "#1677ff" }} />
              <Text strong style={{ color: "#1677ff" }}>
                กรองตามพื้นที่
              </Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                (โซน → จังหวัด → อำเภอ → ตำบล)
              </Text>
            </Space>
          </Col>
          <Col>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleResetArea}
              disabled={!hasAreaFilter}
            >
              ล้างพื้นที่
            </Button>
          </Col>
        </Row>

        <Space size={10} wrap>
          <Select
            {...geoSelectProps}
            placeholder="เลือกโซน"
            value={selZone}
            disabled={lockZone}
            onChange={handleZoneChange}
            popupMatchSelectWidth={false}
            notFoundContent="ไม่มีข้อมูล"
          >
            {zoneOptions.map((o) => (
              <Option key={o.value} value={o.value}>
                {o.label}
              </Option>
            ))}
          </Select>

          <Select
            {...geoSelectProps}
            placeholder="เลือกจังหวัด"
            value={selProvince}
            disabled={lockProvince}
            onChange={handleProvinceChange}
            notFoundContent="ไม่มีข้อมูล"
          >
            {provinceOptions.map((o) => (
              <Option key={o.value} value={o.value}>
                {o.label}
              </Option>
            ))}
          </Select>

          <Select
            {...geoSelectProps}
            placeholder="เลือกอำเภอ"
            value={districtValue}
            disabled={lockDistrict}
            onChange={handleDistrictChange}
            notFoundContent="ไม่มีข้อมูล"
          >
            {districtOptions.map((o) => (
              <Option key={o.value} value={o.value}>
                {o.label}
              </Option>
            ))}
          </Select>

          <Select
            {...geoSelectProps}
            placeholder={
              isIdCardMode
                ? "บัตรประชาชนไม่มีตำบล"
                : selProvince == null
                  ? "เลือกจังหวัดก่อน"
                  : "เลือกตำบล"
            }
            value={subDistrictValue}
            disabled={isIdCardMode || selProvince == null}
            onChange={handleSubDistrictChange}
            notFoundContent="ไม่มีข้อมูล"
          >
            {subDistrictOptions.map((o) => (
              <Option key={o.value} value={o.value}>
                {o.label}
              </Option>
            ))}
          </Select>
        </Space>
      </Card>

      {/* ========== Table (group + expandable) ========== */}
      <Card>
        <Table
          size="small"
          columns={columns}
          dataSource={groupedTable}
          rowKey={(g) => g.groupKey}
          scroll={{ x: 1000 }}
          rowSelection={processStatus === 1 ? rowSelection : null}
          expandable={{
            expandedRowRender,
            rowExpandable: (g) => g.details?.length > 0,
          }}
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `ทั้งหมด ${total} กลุ่มทรัพย์`,
            pageSizeOptions: ["10", "20", "50", "100"],
          }}
          footer={() => (
            <p style={{ margin: 0 }}>
              จำนวนกลุ่มทรัพย์ {groupedTable.length} | จำนวนทรัพย์ทั้งหมด{" "}
              {totalLands}
              {processStatus === 1 &&
                ` | เลือก ${selectedRowKeys.length} กลุ่ม`}
            </p>
          )}
        />
      </Card>
    </Spin>
  );
};

const MainSelectLocationInvestigate = MotionHoc(Main);
export default MainSelectLocationInvestigate;
